/**
 * E07-F12 — small presentational pieces shared by the Jev admin screens
 * (field / button classes, card, pill, section heading). Tokens only, no hex.
 */
import type { ReactNode } from 'react';

export const FIELD_CLASS =
  'w-full rounded border border-border px-3 py-2 text-sm focus:border-primary focus:outline-none';

export const BTN_PRIMARY =
  'rounded bg-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50';

export const BTN_OUTLINE =
  'rounded border border-border px-3 py-1.5 text-xs font-medium text-text-strong hover:bg-gray-50 disabled:opacity-50';

export const BTN_OUTLINE_PRIMARY =
  'rounded border border-primary px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary hover:text-white disabled:opacity-50';

/** A paid action: gold accent + coin icon, so it never reads as a free button. */
export const BTN_PAID =
  'inline-flex items-center gap-2 rounded bg-accent px-4 py-2 text-sm font-semibold text-primary hover:bg-accent-hover disabled:opacity-50';

export function Card({
  children,
  testId,
  className = '',
}: {
  readonly children: ReactNode;
  readonly testId?: string;
  readonly className?: string;
}): React.JSX.Element {
  return (
    <section
      data-testid={testId}
      className={`rounded-lg border border-border bg-white p-4 ${className}`}
    >
      {children}
    </section>
  );
}

export function SectionHeading({
  title,
  hint,
  right,
}: {
  readonly title: string;
  readonly hint?: ReactNode;
  readonly right?: ReactNode;
}): React.JSX.Element {
  return (
    <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h3 className="text-base font-semibold text-text-strong">{title}</h3>
        {hint ? <p className="mt-1 max-w-3xl text-xs text-text-muted">{hint}</p> : null}
      </div>
      {right ? <div className="flex shrink-0 items-center gap-2">{right}</div> : null}
    </div>
  );
}

export type PillTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'primary';

const PILL_TONE: Record<PillTone, string> = {
  neutral: 'bg-gray-100 text-gray-600',
  success: 'bg-green-100 text-green-700',
  warning: 'bg-amber-100 text-amber-800',
  danger: 'bg-red-100 text-red-700',
  info: 'bg-blue-100 text-blue-700',
  primary: 'bg-[var(--color-primary)] text-white',
};

export function Pill({
  children,
  tone = 'neutral',
  testId,
}: {
  readonly children: ReactNode;
  readonly tone?: PillTone;
  readonly testId?: string;
}): React.JSX.Element {
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ${PILL_TONE[tone]}`}
    >
      {children}
    </span>
  );
}

/** Left-bordered notice (warning / info / danger), the app's status-strip pattern. */
export function Notice({
  tone,
  children,
  testId,
}: {
  readonly tone: 'warning' | 'info' | 'danger' | 'success';
  readonly children: ReactNode;
  readonly testId?: string;
}): React.JSX.Element {
  const cls = {
    warning: 'border-[var(--color-warning)] bg-amber-50 text-amber-900',
    info: 'border-[var(--color-info)] bg-blue-50 text-blue-900',
    danger: 'border-[var(--color-danger)] bg-red-50 text-red-800',
    success: 'border-[var(--color-success)] bg-green-50 text-green-800',
  }[tone];
  const icon = {
    warning: 'fa-triangle-exclamation',
    info: 'fa-circle-info',
    danger: 'fa-circle-xmark',
    success: 'fa-circle-check',
  }[tone];
  return (
    <div
      data-testid={testId}
      role={tone === 'danger' ? 'alert' : undefined}
      className={`flex gap-2 border-l-4 px-3 py-2 text-xs ${cls}`}
    >
      <i className={`fa-solid ${icon} mt-0.5`} aria-hidden />
      <div className="min-w-0">{children}</div>
    </div>
  );
}

export function Loading({ testId }: { readonly testId: string }): React.JSX.Element {
  return (
    <div data-testid={testId} className="py-6 text-center text-sm text-gray-400">
      Loading…
    </div>
  );
}

/** A probability bar 0..1 (the top answer is drawn in the primary colour). */
export function ProbBar({
  p,
  top,
}: {
  readonly p: number;
  readonly top: boolean;
}): React.JSX.Element {
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100" aria-hidden>
      <div
        className={top ? 'h-full bg-[var(--color-primary)]' : 'h-full bg-gray-300'}
        style={{ width: `${Math.max(2, Math.round(p * 100))}%` }}
      />
    </div>
  );
}
